import { useEffect, useState, ChangeEvent, useMemo } from 'react'
import {
  Card,
  Page,
  Autocomplete,
  Input,
  Typography,
  Checkbox,
  DatePicker,
  Info,
  UploadArea,
  Alert,
  SlimButton,
  Icon,
  Breadcrumb,
  Modal,
  LoadingModal,
  Accordion
} from 'components'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'store/store'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { formatDate } from 'commons/utils/date'
import {
  clearGeneratedData,
  clearUploadData,
  deleteEdiUpload,
  getEdiOptions,
  getEdiVersionOptions,
  SendEdiOrders,
  sendEdiUpload
} from 'store/BatchOrderContext/BatchOrderSlice'
import { EdiFormValues, EdiVersion, IntlEDI } from 'services/batchOrder/interfaces'
import { getUser } from 'commons/utils/user'
import UploadSpreadsheetTableError from '../components/UploadSpreadsheetTableError'
import Footer from '../components/Footer'
import ModalOrderOverview from '../components/ModalOrderOverview'
import { ROUTES } from 'constants/urls'
import { useNavigate } from 'react-router-dom'

import * as S from './styles'

import { removeDiacriticsAndSpecialCharacters } from 'commons/utils/string'
import { OverviewContent } from './components'
import { theme } from 'styles/theme/hav-theme'

const International = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    loadingEdiOptions,
    ediOptions,
    loadingVersionOptions,
    versionOptions,
    loadingUpload,
    loadingDeletion,
    generatedEdiOrders,
    loadingSend,
    uploadData: { processId, ediErrors, uploadSuccess, hasOrders }
  } = useSelector((state) => state.batchOrder)

  const schema = yup.object().shape({
    edi: yup.object().typeError('Seleção obrigatória').required(),
    checkVersion: yup.boolean(),
    version: yup.object().when('checkVersion', {
      is: true,
      then: yup.object().typeError('Seleção obrigatória').required(),
      otherwise: yup.object().nullable()
    }),
    warehouse: yup.string(),
    orderMonth: yup.date().typeError('Data inválida').required(),
    billingMonth: yup.date().typeError('Data inválida').required(),
    expectedDate: yup.date().typeError('Data inválida').required(),
    headerText: yup.string().required('Campo obrigatório'),
    itemText: yup.string().required('Campo obrigatório')
  })

  const defaultValues: EdiFormValues = {
    edi: null,
    checkVersion: false,
    version: null,
    warehouse: '',
    orderMonth: '',
    billingMonth: '',
    expectedDate: '',
    headerText: '',
    itemText: '',
    uploadedFile: null
  }

  const {
    control,
    watch,
    setValue,
    trigger,
    handleSubmit,
    formState: { isValid }
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  const disableForm = useMemo(
    () => loadingUpload || !!processId,
    [loadingUpload, processId]
  )
  const hasUploadSuccess = useMemo(() => !!processId && hasOrders, [processId, hasOrders])
  const hasUploadError = useMemo(
    () => !!processId && !!ediErrors.length,
    [processId, hasOrders]
  )

  const selectedEDI = watch('edi')
  const checkVersion = watch('checkVersion')
  const fileName = watch('uploadedFile.name')

  const [ediSearch, setEdiSearch] = useState('')
  const [versionSearch, setVersionSearch] = useState('')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [modalDeleteOrders, setModalDeleteOrders] = useState(false)
  const [modalOrderOverview, setModalOrderOverview] = useState(false)

  const onSubmit = handleSubmit((data) => {
    dispatch(sendEdiUpload(data, setUploadProgress))
  })

  useEffect(() => {
    dispatch(getEdiOptions())

    return () => {
      dispatch(clearUploadData())
    }
  }, [])

  useEffect(() => {
    if (selectedEDI) {
      dispatch(getEdiVersionOptions(selectedEDI.cd_edi))
    }
  }, [selectedEDI])

  useEffect(() => {
    trigger('version')
  }, [checkVersion])

  useEffect(() => {
    if (generatedEdiOrders.id_prcs_edi) {
      setModalOrderOverview(true)
    }
  }, [generatedEdiOrders])

  const handleOrderDeletion = () => {
    setModalDeleteOrders(false)
    dispatch(
      deleteEdiUpload({
        cd_edi: selectedEDI?.cd_edi,
        id_prcs_edi: processId,
        cd_usua_alte: getUser()
      })
    )
  }

  const handleOrderSend = () => {
    dispatch(
      SendEdiOrders({
        cd_edi: selectedEDI?.cd_edi,
        id_prcs_edi: processId
      })
    )
  }

  const getVersionPlaceholder = () => {
    if (!selectedEDI) return 'Selecione um EDI'
    if (loadingVersionOptions) return 'Carregando opções...'
    if (!checkVersion) return 'Ative o checkbox'
    return 'Clique para selecionar'
  }

  const InsertionHeader = () => (
    <S.ColWrapper>
      <Typography design="hav-h6-text">Inserção de material</Typography>
      <Typography design="hav-h7-text">
        Após preencher os campos acima, carregue a planilha e solicite os materiais.
      </Typography>
    </S.ColWrapper>
  )

  return (
    <Page fullwidth>
      <Breadcrumb />
      <S.Wrapper>
        <Accordion summaryContent={<S.Title>Pedido internacional</S.Title>}>
          <Card className="form">
            <S.Form onSubmit={onSubmit}>
              <S.Field span={4}>
                <Controller
                  name={'edi'}
                  control={control}
                  render={({ field }) => (
                    <Autocomplete<IntlEDI>
                      label="EDI"
                      ref={field.ref}
                      inputId={field.name}
                      objId="cd_edi"
                      objDescription="ds_edi"
                      data={ediOptions}
                      setOptionSelected={(e) => {
                        setValue('version', null)
                        setVersionSearch('')
                        field.onChange(e)
                      }}
                      placeHolder={
                        loadingEdiOptions
                          ? 'Carregando opções...'
                          : 'Clique para selecionar'
                      }
                      input={ediSearch}
                      setInput={setEdiSearch}
                      disabled={disableForm || loadingEdiOptions || !ediOptions.length}
                      browserAutocomplete="off"
                      resetOnBlur
                    />
                  )}
                />
              </S.Field>
              <S.Field span={1}>
                <S.CubageWrapper>
                  <Typography design="hav-h8-text">M² Máxima OV</Typography>
                  <Typography design="hav-default-03-text">
                    {selectedEDI?.vl_maxi_metr_cubi ?? '-'}
                  </Typography>
                </S.CubageWrapper>
              </S.Field>
              <S.Field span={1}>
                <S.VersionCheckWrapper>
                  <Controller
                    name={'checkVersion'}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        label="Definir versão"
                        size="small"
                        checked={field.value}
                        onChange={(event, checked) => {
                          if (!checked) {
                            setValue('version', null)
                            setVersionSearch('')
                          }
                          field.onChange(event)
                        }}
                        disabled={disableForm || !selectedEDI}
                      />
                    )}
                  />
                </S.VersionCheckWrapper>
              </S.Field>
              <S.Field>
                <Controller
                  name={'version'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete<EdiVersion>
                      label="Versão"
                      ref={field.ref}
                      inputId={field.name}
                      objId="cd_edi_vers"
                      objDescription="ds_edi_vers"
                      data={versionOptions}
                      setOptionSelected={field.onChange}
                      placeHolder={getVersionPlaceholder()}
                      input={versionSearch}
                      setInput={setVersionSearch}
                      disabled={
                        disableForm ||
                        !checkVersion ||
                        loadingVersionOptions ||
                        !versionOptions.length
                      }
                      browserAutocomplete="off"
                      resetOnBlur={field.value !== null}
                      error={fieldState.error?.message}
                      inputAdornment={
                        <Info iconSize={12}>
                          Caso o campo de versão não seja preenchido, o sistema
                          selecionará automaticamente a coleção atual.
                        </Info>
                      }
                    />
                  )}
                />
              </S.Field>
              <S.Field>
                <Controller
                  name={'warehouse'}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="Armazém"
                      placeholder={
                        !selectedEDI ? 'Selecione um EDI' : 'Clique para escrever'
                      }
                      onChange={(e) =>
                        field.onChange(
                          removeDiacriticsAndSpecialCharacters(e.target.value)
                        )
                      }
                      showMaxLength
                      maxLength={25}
                      maxLenghtPosition="top"
                      disabled={disableForm || !selectedEDI}
                    />
                  )}
                />
              </S.Field>
              <S.Field>
                <Controller
                  name={'orderMonth'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label="Mês do pedido"
                      views={['month', 'year']}
                      inputFormat="MM/YYYY"
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'mm/aaaa'}
                      value={field.value}
                      onChange={(date) => field.onChange(formatDate(date, 'YYYY-MM'))}
                      disableMaskedInput={false}
                      height={30}
                      disabled={disableForm || !selectedEDI}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </S.Field>
              <S.Field>
                <Controller
                  name={'billingMonth'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label="Mês do faturamento"
                      views={['month', 'year']}
                      inputFormat="MM/YYYY"
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'mm/aaaa'}
                      value={field.value}
                      onChange={(date) => field.onChange(formatDate(date, 'YYYY-MM'))}
                      disableMaskedInput={false}
                      height={30}
                      disabled={disableForm || !selectedEDI}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </S.Field>
              <S.Field>
                <Controller
                  name={'expectedDate'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label="Data prevista"
                      inputFormat="DD/MM/YYYY"
                      placeholder={!selectedEDI ? 'Selecione um EDI' : 'dd/mm/aaaa'}
                      value={field.value}
                      onChange={(date) => field.onChange(formatDate(date, 'YYYY-MM-DD'))}
                      disableMaskedInput={false}
                      height={30}
                      disabled={disableForm || !selectedEDI}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </S.Field>
              <S.Field span={4}>
                <Controller
                  name={'headerText'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      label="Cabeçalho"
                      maxLength={140}
                      showMaxLength
                      maxLenghtPosition="top"
                      placeholder={
                        !selectedEDI ? 'Selecione um EDI' : 'Clique para escrever'
                      }
                      onChange={(e) =>
                        field.onChange(
                          removeDiacriticsAndSpecialCharacters(e.target.value)
                        )
                      }
                      disabled={disableForm || !selectedEDI}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </S.Field>
              <S.Field span={4}>
                <Controller
                  name={'itemText'}
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      label="Item"
                      maxLength={140}
                      showMaxLength
                      maxLenghtPosition="top"
                      placeholder={
                        !selectedEDI ? 'Selecione um EDI' : 'Clique para escrever'
                      }
                      onChange={(e) =>
                        field.onChange(
                          removeDiacriticsAndSpecialCharacters(e.target.value)
                        )
                      }
                      disabled={disableForm || !selectedEDI}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </S.Field>
            </S.Form>
          </Card>
        </Accordion>

        {hasUploadError && (
          <Alert
            kind="error"
            title="Erros na planilha!"
            message="Foram identificados alguns erros na planilha. Verifique os detalhes abaixo e faça as correções necessárias antes de reenviá-la."
            fullWidth
          />
        )}
        {hasUploadSuccess && (
          <Alert
            kind="info"
            title="Pedido em aberto!"
            message='Clique em "solicitar" para gerar as OVs. Caso contrário, os pedidos ficarão em aberto na área de gerenciamento.'
            fullWidth
          />
        )}
        {!processId && (
          <Card>
            <S.InsertionWrapper>
              <InsertionHeader />
              {isValid && (
                <>
                  <S.Divider />
                  <Controller
                    name="uploadedFile"
                    control={control}
                    defaultValue={{} as File}
                    render={({ field: { name, onChange } }) => (
                      <UploadArea
                        id="uploadedFile"
                        name={name}
                        accept="text/csv"
                        loading={loadingUpload}
                        loadingProgress={uploadProgress}
                        uploadAreaText={
                          <>
                            <strong>Clique aqui</strong> ou arraste o arquivo da sua
                            planilha para enviá-la.
                          </>
                        }
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.files) {
                            onChange(e.target.files[0])
                            onSubmit()
                            e.target.value = ''
                          }
                        }}
                      />
                    )}
                  />
                </>
              )}
            </S.InsertionWrapper>
          </Card>
        )}
        {hasUploadError && (
          <Card>
            <S.ErrorWrapper>
              <div>
                <SlimButton
                  type="button"
                  variant="primary"
                  content="Voltar"
                  icon={<Icon icon="undo" size="15" />}
                  onClick={() => dispatch(clearUploadData())}
                />
              </div>
              <S.UploadedFileViewer>
                <Icon icon="paper-clip" color={theme.colors.primary.main} size="15" />
                <Typography design="hav-h7-text">{fileName}</Typography>
              </S.UploadedFileViewer>
              <UploadSpreadsheetTableError data={ediErrors} />
            </S.ErrorWrapper>
          </Card>
        )}
        {hasUploadSuccess && (
          <Card>
            <S.InsertionWrapper>
              <InsertionHeader />
              <S.Divider />
              <S.UploadedFileViewer>
                <Icon icon="paper-clip" color={theme.colors.primary.main} size="15" />
                <Typography design="hav-h7-text">{fileName}</Typography>
              </S.UploadedFileViewer>
              <Footer
                amountOfPairs={uploadSuccess.pairVol}
                amountOfBoxes={uploadSuccess.boxVol}
                totalValue={uploadSuccess.totalVal}
                loading={loadingDeletion || loadingSend}
                onDelete={() => setModalDeleteOrders(true)}
                onSubmit={handleOrderSend}
              />
            </S.InsertionWrapper>
          </Card>
        )}
      </S.Wrapper>
      <Modal
        open={modalDeleteOrders}
        setOpen={setModalDeleteOrders}
        title="Excluir"
        showAction={false}
        description="Esta ação excluirá o pedido em aberto e não poderá ser desfeita."
      >
        <S.DeleteOrdersActions>
          <SlimButton
            variant="ghost"
            content="Voltar"
            onClick={() => setModalDeleteOrders(false)}
          />
          <SlimButton
            variant="danger"
            content="Excluir"
            onClick={handleOrderDeletion}
            loading={loadingDeletion}
          />
        </S.DeleteOrdersActions>
      </Modal>
      {loadingSend && <LoadingModal loadingText="Solicitando materiais..." />}
      {!!generatedEdiOrders.id_prcs_edi && (
        <ModalOrderOverview
          open={modalOrderOverview}
          setOpen={setModalOrderOverview}
          onConfirm={() => {
            dispatch(clearGeneratedData())
            navigate(`${ROUTES.manage.base}`)
          }}
          onReturn={() => {
            dispatch(clearGeneratedData())
            setModalOrderOverview(false)
          }}
        >
          <OverviewContent />
        </ModalOrderOverview>
      )}
    </Page>
  )
}

export default International
